'use client'

import React, { useEffect, useState } from 'react'
import { WagamourV2Modal } from '@/components/v2/WagamourV2Modal'
import {
  WagamourV2ModalEyebrow,
  WagamourV2ModalStoryLine,
} from '@/components/v2/WagamourV2ModalContent'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import {
  v2ModalErrorBannerStyle,
  v2ModalGeometry,
  v2ModalStyles,
  v2ModalWarningCalloutStyle,
} from '@/lib/design/v2ModalContent'
import { playV2FocusRing } from '@/lib/play/playV2Focus'
import { cn } from '@/lib/utils'
import { formatPlayActivityRelativeTime } from '@/lib/play/formatPlayActivityRelativeTime'
import {
  dedupePlayUpdateTimelineItems,
  resolvePlayUpdateInvitationPresentation,
  resolvePlayUpdateNavigation,
} from '@/lib/play/playUpdateNavigation'

export type PlayUpdateListItem = {
  id: string
  type: string
  summary: string
  createdAt: string
  readAt: string | null
  playdateId: string | null
  relationshipId?: string | null
}

export type PlayIntegratedUpdatesSheetProps = {
  isOpen: boolean
  count: number
  onClose: () => void
  /** When provided, loads the real Updates ledger. */
  dogId?: string | null
  playdatesEnabled?: boolean
  /** Live CirclePlaydate status by id — drives invitation CTA copy. */
  livePlaydateStatusById?: Readonly<Record<string, string | undefined>>
  /** Navigate into relationship / playdate review from a ledger row. */
  onSelectUpdate?: (item: PlayUpdateListItem) => void
}

export function PlayIntegratedUpdatesSheet({
  isOpen,
  count,
  onClose,
  dogId = null,
  playdatesEnabled = false,
  livePlaydateStatusById = {},
  onSelectUpdate,
}: PlayIntegratedUpdatesSheetProps) {
  const [items, setItems] = useState<PlayUpdateListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !playdatesEnabled || !dogId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    void fetch(`/api/play/updates?dogId=${encodeURIComponent(dogId)}`)
      .then(async (res) => {
        const json = (await res.json().catch(() => ({}))) as {
          success?: boolean
          items?: PlayUpdateListItem[]
          error?: string
        }
        if (cancelled) return
        if (!res.ok || !json.success) {
          setError(json.error ?? 'Could not load updates')
          setItems([])
          return
        }
        setItems(dedupePlayUpdateTimelineItems(json.items ?? []))
      })
      .catch(() => {
        if (!cancelled) setError('Network error — try again')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, playdatesEnabled, dogId])

  return (
    <WagamourV2Modal
      isOpen={isOpen}
      onClose={onClose}
      presentation="sheet"
      ariaLabel="Updates"
      header={
        <div className="space-y-1">
          <WagamourV2ModalEyebrow>Play activity</WagamourV2ModalEyebrow>
          <h2 className="text-base font-bold leading-snug" style={v2ModalStyles.headline}>
            Updates
          </h2>
        </div>
      }
    >
      {!playdatesEnabled || !dogId ? (
        <>
          <WagamourV2ModalStoryLine label="Unread">
            {`${count} update${count === 1 ? '' : 's'}`}
          </WagamourV2ModalStoryLine>
          <p className="mt-3 text-sm leading-relaxed" style={v2ModalStyles.muted}>
            Invitations and relationship activity appear here when playdates are available for
            this dog.
          </p>
        </>
      ) : (
        <div className="space-y-3" data-play-updates-ledger="true">
          {loading ? (
            <p className="text-sm" style={v2ModalStyles.muted}>
              Loading updates…
            </p>
          ) : null}
          {error ? (
            <div className="space-y-2">
              <p
                className="rounded-[16px] border px-3 py-2 text-sm"
                role="alert"
                style={v2ModalErrorBannerStyle()}
              >
                {error}
              </p>
              <WagamourV2Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => {
                  setError(null)
                  setLoading(true)
                  void fetch(`/api/play/updates?dogId=${encodeURIComponent(dogId)}`)
                    .then(async (res) => {
                      const json = (await res.json().catch(() => ({}))) as {
                        success?: boolean
                        items?: PlayUpdateListItem[]
                        error?: string
                      }
                      if (!res.ok || !json.success) {
                        setError(json.error ?? 'Could not load updates')
                        return
                      }
                      setItems(dedupePlayUpdateTimelineItems(json.items ?? []))
                    })
                    .catch(() => setError('Network error — try again'))
                    .finally(() => setLoading(false))
                }}
              >
                Retry
              </WagamourV2Button>
            </div>
          ) : null}
          {!loading && !error && items.length === 0 ? (
            <div
              className="space-y-1.5 rounded-[20px] border px-4 py-3"
              style={v2ModalWarningCalloutStyle()}
              data-play-updates-empty="true"
            >
              <p className="text-sm font-medium" style={v2ModalStyles.body}>
                No updates yet
              </p>
              <p className="text-sm leading-relaxed" style={v2ModalStyles.muted}>
                When a friend proposes a playdate or responds, it shows up here.
              </p>
            </div>
          ) : null}
          {!loading &&
            !error &&
            items.map((item) => {
              const nav = resolvePlayUpdateNavigation(item)
              const invitation = resolvePlayUpdateInvitationPresentation(
                item,
                livePlaydateStatusById
              )
              const actionable = Boolean(onSelectUpdate && nav.kind !== 'none')
              const relative = formatPlayActivityRelativeTime(item.createdAt)
              const actionLabel = invitation.actionLabel
              const body = (
                <>
                  <p className="text-sm" style={v2ModalStyles.body}>
                    {item.summary}
                  </p>
                  <p className="mt-1 text-[11px]" style={v2ModalStyles.muted}>
                    {relative}
                  </p>
                  {actionable ? (
                    <p className="mt-1.5 text-[11px] font-medium" style={v2ModalStyles.warm}>
                      {actionLabel}
                    </p>
                  ) : null}
                </>
              )
              if (!actionable) {
                return (
                  <div
                    key={item.id}
                    className={cn('border p-3', v2ModalGeometry.nestedRadius)}
                    style={v2ModalStyles.nestedCard}
                    data-play-update-item={item.type}
                    data-play-update-invitation={
                      invitation.openInvitation ? 'open' : 'settled'
                    }
                    data-playdate-id={item.playdateId ?? undefined}
                    data-relationship-id={item.relationshipId ?? undefined}
                  >
                    {body}
                  </div>
                )
              }
              return (
                <button
                  key={item.id}
                  type="button"
                  className={cn(
                    'w-full border p-3 text-left transition hover:border-amber-200/35',
                    v2ModalGeometry.nestedRadius,
                    playV2FocusRing
                  )}
                  style={v2ModalStyles.nestedCard}
                  data-play-update-item={item.type}
                  data-play-update-actionable="true"
                  data-play-update-invitation={
                    invitation.openInvitation ? 'open' : 'settled'
                  }
                  data-playdate-id={item.playdateId ?? undefined}
                  data-relationship-id={item.relationshipId ?? undefined}
                  aria-label={`${actionLabel}: ${item.summary}`}
                  onClick={() => onSelectUpdate?.(item)}
                >
                  {body}
                </button>
              )
            })}
        </div>
      )}
    </WagamourV2Modal>
  )
}
